import React, { useMemo, useState } from "react";
import {
  FaUtensils,
  FaHamburger,
  FaIceCream,
  FaCoffee,
  FaCocktail,
  FaWineBottle,
  FaClipboardList,
  FaTimes,
  FaBeer,
  FaPlaystation,
  FaBlender,
  FaLeaf,
  FaDog,
} from "react-icons/fa";
import { FaBottleWater } from "react-icons/fa6";
import { TbCakeRoll } from "react-icons/tb";
import { PiBowlFoodFill, PiPopsicleFill } from "react-icons/pi";
import logo from "../Logos/logodios.png";
//import { GiMilkshake } from "react-icons/gi";
import "./HomePage.css";
import FloatingCart from "./FloatingCart";
import { BASE_URL } from "./util/formatUtils";

/* ======================
   Helpers extras agrupados
====================== */
function groupByCategory(list = []) {
  return list.reduce((acc, x) => {
    const cat = (x?.categoria || "Otros").trim() || "Otros";
    const name = (x?.nombre || "").trim();
    if (!name) return acc;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(name);
    return acc;
  }, {});
}

function GroupedExtras({ title, list }) {
  if (!list?.length) return null;
  const grouped = groupByCategory(list);
  const cats = Object.keys(grouped).sort((a, b) => a.localeCompare(b, "es"));

  return (
    <div className="extras-block">
      <div className="extras-title">{title}</div>
      {cats.map((cat) => (
        <div className="extra-cat" key={cat}>
          <div className="extra-cat-title">{cat}</div>
          <div className="extra-cat-items">{grouped[cat].join(", ")}</div>
        </div>
      ))}
    </div>
  );
}

/* ======================
   Componentes Modales
====================== */
function ModalShell({ onClose, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function PedidoFormModal({
  open,
  onClose,
  mesa,
  setMesa,
  nombre,
  setNombre,
  loading,
  err,
  onSubmit,
}) {
  if (!open) return null;

  return (
    <ModalShell onClose={onClose}>
      <div className="modal-head">
        <h3>Ver pedido de mesa</h3>
        <button className="modal-close" onClick={onClose}>
          <FaTimes />
        </button>
      </div>

      <form onSubmit={onSubmit} className="modal-body">
        <label className="modal-label">
          Mesa
          <input
            className="modal-input"
            value={mesa}
            onChange={(e) => setMesa(e.target.value)}
            placeholder="Ej: 12"
            autoFocus
          />
        </label>

        <label className="modal-label">
          Nombre
          <input
            className="modal-input"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Ej: Cristian"
          />
        </label>

        {err ? <div className="modal-error">{err}</div> : null}

        <div className="modal-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? "Consultando..." : "Enviar"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function PedidoDetalleModal({ open, onClose, pedido }) {
  if (!open) return null;

  return (
    <ModalShell onClose={onClose}>
      <div className="modal-head">
        <h3>Detalle del pedido</h3>
        <button className="modal-close" onClick={onClose}>
          <FaTimes />
        </button>
      </div>

      {/* ✅ ESTE ES EL CONTENEDOR SCROLL */}
      <div className="modal-scroll">
        <div className="modal-result">
          <div className="result-row">
            <b>Mesa:</b> {pedido?.pedido?.mesa}
          </div>
          <div className="result-row">
            <b>Cliente:</b> {pedido?.pedido?.nombre}
          </div>
          <div className="result-row">
            <b>Total:</b> {pedido?.pedido?.total_formatted}
          </div>
          <div className="result-row">
            <b>Pagado:</b> {pedido?.pedido?.pagado_formatted}
          </div>
          <div className="result-row deuda">
            <b>Deuda:</b> {pedido?.pedido?.deuda_formatted}
          </div>

          <div className="result-items">
            <b>Items:</b>

            {(pedido?.items || []).map((it, idx) => (
              <div key={it.id ?? idx} className="result-item">
                <div className="result-item-row">
                  <span>
                    {it.qty} x {it.name}
                  </span>
                  <span>{it.subtotal_formatted}</span>
                </div>

                {(it.toppings?.length > 0 ||
                  it.complementos?.length > 0 ||
                  (it.comentario || "").trim()) && (
                  <div className="result-item-extras">
                    <GroupedExtras title="Toppings" list={it.toppings} />
                    <GroupedExtras
                      title="Complementos"
                      list={it.complementos}
                    />

                    {(it.comentario || "").trim() && (
                      <div className="extras-comment">
                        <b>Nota:</b> {it.comentario}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

/* ======================
   HomePage
====================== */
export default function HomePage({ menu, onSelectCategory }) {
  const categories = useMemo(
    () => [...new Set(menu.map((item) => item.category))],
    [menu],
  );

  const categoryIcons = {
    "Platos Fuertes": <FaUtensils />,
    "Comida Rapida": <FaHamburger />,
    Entradas: <PiBowlFoodFill />,
    Vegetarianos: <FaLeaf />,
    Mascotas: <FaDog />,
    Postres: <TbCakeRoll />,
    Cocteles: <FaCocktail />,
    Malteadas: <FaIceCream />,
    Juegos: <FaPlaystation />,
    "Bebidas Frias": <FaBottleWater />,
    "Bebidas Calientes": <FaCoffee />,
    Licores: <FaWineBottle />,
    "Cerveza Artesanal": <FaBeer />,
    Sushi: (
      <img
        src={logo}
        alt="logo"
        style={{ width: 80, height: 20, objectFit: "contain" }}
      />
    ),
  };

  // ✅ Modal states (2 modales)
  const [openForm, setOpenForm] = useState(false);
  const [openDetalle, setOpenDetalle] = useState(false);

  const [mesa, setMesa] = useState("");
  const [nombre, setNombre] = useState("");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [pedido, setPedido] = useState(null);

  const closeForm = () => {
    setOpenForm(false);
    setErr("");
    setLoading(false);
  };

  const closeDetalle = () => {
    setOpenDetalle(false);
    setPedido(null);
    setMesa("");
    setNombre("");
    setErr("");
    setLoading(false);
  };

  const consultarPedido = async (e) => {
    e.preventDefault();
    setErr("");

    const mesaTrim = mesa.trim();
    const nombreTrim = nombre.trim();

    if (!mesaTrim) return setErr("Ingresa el número o código de la mesa.");
    if (!nombreTrim) return setErr("Ingresa el nombre del cliente.");

    setLoading(true);

    try {
      const resp = await fetch(`${BASE_URL}Carta/consultapedido.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mesa: mesaTrim, nombre: nombreTrim }),
      });

      const data = await resp.json().catch(() => null);

      if (!resp.ok || !data)
        throw new Error("No se pudo leer la respuesta del servidor.");
      if (!data.success) {
        setErr(data.message || "No se encontró pedido para esa mesa.");
        setLoading(false);
        return;
      }

      // ✅ Éxito: guardas pedido, cierras modal 1 y abres modal 2
      setPedido(data);
      setLoading(false);
      setOpenForm(false);
      setOpenDetalle(true);
    } catch (error) {
      setErr(error.message || "Error consultando el pedido.");
      setLoading(false);
    }
  };

  return (
    <>
      <div className="home-container">
        <div className="scroll-indicator">
          <span>Desliza</span>
          <div className="scroll-arrow"></div>
        </div>
        {/* BOTÓN “VER PEDIDO DE MESA” */}
        <div className="home-actions">
          <button className="table-order-btn" onClick={() => setOpenForm(true)}>
            <FaClipboardList />
            <span>Ver mesa</span>
          </button>
        </div>

        {/* CARDS DE CATEGORÍAS */}
        {categories.map((cat) => (
          <div
            key={cat}
            className="home-card"
            onClick={() => onSelectCategory(cat)}
          >
            <div className="home-overlay">
              <div className="home-content">
                <span className="home-icon">
                  {categoryIcons[cat] || <FaUtensils />}
                </span>
                <h2 className="home-title">{cat}</h2>
              </div>
            </div>
          </div>
        ))}

        {/* MODAL 1: FORM */}
        <PedidoFormModal
          open={openForm}
          onClose={closeForm}
          mesa={mesa}
          setMesa={setMesa}
          nombre={nombre}
          setNombre={setNombre}
          loading={loading}
          err={err}
          onSubmit={consultarPedido}
        />

        {/* MODAL 2: DETALLE (SCROLL) */}
        <PedidoDetalleModal
          open={openDetalle}
          onClose={closeDetalle}
          pedido={pedido}
        />
      </div>
      <FloatingCart />
    </>
  );
}
